import React, { useEffect, useState } from "react";
import "./RequiredWorkStyles.scss";
import config from "clientappsetting.json";

import { Api } from "api/Base/Api";
import { CommandDetails } from "api/Base/CommandDetails";
import { LoqateConfig } from "./LoqateConfig";

import dayjs from "dayjs";
import "dayjs/locale/da";
import "dayjs/locale/en";
import * as yup from "yup";
import { Form as FormikForm, Formik, Field } from "formik";
import { Button, Modal } from "react-bootstrap";
import ContentLoader from "react-content-loader";

import { Checkbox, CheckboxGroup } from "components/generic/CheckboxGroup";
import { FormikFormRow } from "components/generic/FormRow";
import { FormikField } from "components/generic/FormikField";
import { Hero } from "components/generic/Hero";
import { RadioButtonProps } from "components/generic/RadioButton";
import { RadioButtonGroup } from "components/generic/RadioButtonGroup";
import { scrollToTop } from "helpers/ScrollingHelper";
import { INewBookingPropsBase } from "types/INewBookingPropsBase";
import {
  AdditionalServiceType,
  MultipleDbmBookingWorkTypeRuleEvaluationRequest,
  RuleTableReference,
  ShowHideOrMessageResult,
  VehicleDetails,
  WorkType,
} from "generated/types/types";
import { WorkDetails } from "types/NewBooking";
import { DropdownStyleExpander } from "components/generic/ExpandableSections/DropdownStyleExpander";
import { assertNever } from "helpers/Helpers";
import { ModalCentered } from "components/generic/ModalCentered";
import { SAFETY_RECALL_MESSAGE } from "constants/Constants";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";
import { ApiUrlResolver } from "utils/ApiUrlResolver";
import { useTranslation } from "react-i18next";
import AddressSearch, { type DetailSuggestion, type AddressSearchProps,  makeDefaultStrings,} from "../../components/AddressSearch";
import { mapLoqateGroupsGeneric } from "components/AddressSearch/utils/mappers";
import { stripLeadingDuplicate, stripTrailingCount,
} from "components/AddressSearch/utils/format";

type FormValues = {
  requiredWork: WorkType[];
  repairOtherText: string;
  additionalServices: AdditionalServiceType;
  collectionContactName: string;
  collectionContactNumber: string;
  collectionAddress: string;
  collectionPostCode: string;
  collectionInstructions: string;
  additionalNotes: string;
  safetyRecall: boolean;
  collectionAddressSearch: string;
};

interface Props extends INewBookingPropsBase {
  micrositePin: string;
  currentWorkDetails: WorkDetails | null;
  vehicleDetails: VehicleDetails;
  setWorkDetails: (value: WorkDetails) => void;
  motDueDate: Date | null;
  showMotDueDate: boolean;
  safetyRecallCheck?: boolean;
  noBookingOptionsHelptext?: string;
  noBookingOptionsHelptextUrl?: string;
}

export const RequiredWork: React.FC<Props> = (props: Props) => {
  useEffect(() => scrollToTop(), []);

  const DEBOUNCE_MS =
    (Number(config?.ADDRESS_SEARCH?.DEBOUNCE_MS) || 0) > 0
      ? Number(config.ADDRESS_SEARCH.DEBOUNCE_MS)
      : 300;

  const MIN_CHARS =
    (Number(config?.ADDRESS_SEARCH?.MIN_CHARS) || 0) > 0
      ? Number(config.ADDRESS_SEARCH.MIN_CHARS)
      : 2;

  const [assetJobTypes, setAssetJobTypes] = React.useState<string[]>([]);
  const [assetJobTypeSet, setAssetJobTypeSet] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState<React.ReactNode>(null);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [availableWorkTypesAndOptions, setAvailableWorkTypesAndOptions] =
    useState<
      | {
          [reference in keyof typeof RuleTableReference]: ShowHideOrMessageResult;
        }
      | null
    >(null);
  const [evaluationInProgress, setEvaluationInProgress] =
    useState<boolean>(true);

  const countryCode = useSelector(
    (state: RootState) => state.country.countryCode
  );
  const { t, i18n } = useTranslation();

  useEffect(() => {
  const lang = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
  dayjs.locale(lang.startsWith("da") || lang === "dk" ? "da" : "en");
}, [i18n.language, i18n.resolvedLanguage]);

  const schema = yup.object({
    requiredWork: yup.array().required(t("required_work_select")),
    repairOtherText: yup
      .string()
      .nullable()
      .when("requiredWork", {
        is: (val: WorkType[]) => val.includes(WorkType.RepairOther),
        then: yup
          .string()
          .required(t("required_work_valid_repair"))
          .max(500, t("required_work_max_500")),
        otherwise: yup.string(),
      }),
    collectionAddress: yup
      .string()
      .nullable()
      .when("additionalServices", {
        is: (val: AdditionalServiceType) =>
          val === AdditionalServiceType.Delivery,
        then: yup
          .string()
          .required(t("required_work_valid_delivery_or_collection"))
          .max(500, t("required_work_max_500")),
        otherwise: yup.string(),
      }),
    collectionInstructions: yup
      .string()
      .nullable()
      .when("additionalServices", {
        is: (val: AdditionalServiceType) =>
          val === AdditionalServiceType.Delivery,
        then: yup.string().max(500, t("required_work_max_500")),
        otherwise: yup.string(),
      }),
    collectionContactName: yup
      .string()
      .nullable()
      .when("additionalServices", {
        is: (val: AdditionalServiceType) =>
          val === AdditionalServiceType.Delivery,
        then: yup
          .string()
          .required(t("required_work_contact_name"))
          .max(50, t("required_work_max_50")),
        otherwise: yup.string(),
      }),
    collectionContactNumber: yup
      .string()
      .nullable()
      .when("additionalServices", {
        is: (val: AdditionalServiceType) =>
          val === AdditionalServiceType.Delivery,
        then: yup
          .string()
          .max(30)
          .required(t("required_work_contact_name_required")),
        otherwise: yup.string(),
      }),
    additionalNotes: yup
      .string()
      .nullable()
      .max(2000, t("required_work_max_2000")),
  });

  const clearTempCollection = () => {
    sessionStorage.removeItem("tempCollectionPostcode");
    sessionStorage.removeItem("tempCollectionAddress");
  };

  const getMotDueDateLabel = () => {
    const motIsWithinThreeMonths =
      props.motDueDate &&
      dayjs(new Date()).isAfter(dayjs(props.motDueDate).subtract(3, "month"));

    if (props.showMotDueDate && motIsWithinThreeMonths) {
      return t("required_work_due_date") + dayjs(props.motDueDate).format("ddd Do MMM YYYY");
    }
    return "";
  };

  useEffect(() => {
    const apiUrl = ApiUrlResolver.getWebApiUrl(countryCode);
    const loadBookingWorkTypes = async () => {
 if(countryCode === "DK")
      {
            const response = await fetch(apiUrl  + `/Operator/GetAssetRestrictedOperatorJobTypes/${props.vehicleDetails.assetId}/${props.micrositePin}/${countryCode}`
              , { method: "GET" });
            const jobTypesData: string[] = await response.json();

            setAssetJobTypes(jobTypesData);
            setAssetJobTypeSet(new Set(jobTypesData.map(a => a.toLowerCase())));
      }

    const requestData: MultipleDbmBookingWorkTypeRuleEvaluationRequest = {
      driverMicrositePin: props.micrositePin,
      ruleTableReferences: [
        RuleTableReference.DbmBookingWorkTypeService,
        RuleTableReference.DbmBookingWorkTypeTyres,
        RuleTableReference.DbmBookingWorkTypeMot,
        RuleTableReference.DbmBookingWorkTypeRepair,
        RuleTableReference.DbmBookingBookingTypeDeliveryAndCollection,
        RuleTableReference.DbmBookingBookingTypeCourtesyCar,
        RuleTableReference.DbmBookingBookingTypeWhileUWait,
        RuleTableReference.DbmBookingBookingTypeDropOffVehicle,
      ],
      contextualData: {
        kind: "ClientDbmBookingWorkTypeContext",
        vehicleBeingBooked: {
          encryptedId: props.vehicleDetails.encryptedId,
        },
      },
    };

    const requestDataString = JSON.stringify(requestData);
    const workTypeResponse = await
        fetch(apiUrl + "/RuleEvaluation/DbmBookingWorkTypes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: requestDataString,
          });

        const workTypeData = await workTypeResponse.json();
        setAvailableWorkTypesAndOptions(workTypeData);
        setEvaluationInProgress(false);
    }
    
    loadBookingWorkTypes();

  }, [props.micrositePin, props.vehicleDetails.encryptedId, countryCode]);

  const getCheckboxOptionForWorkType = (
    workTypeResult: ShowHideOrMessageResult,
    workType: WorkType,
    label: string,
    subLabel?: string
  ): Checkbox<WorkType>[] => {
  const assetJobTypeExists = countryCode === "DK" ? 
                              assetJobTypeSet.has(workType.toLowerCase()) 
                                          : true;
  if(assetJobTypeExists || workTypeResult.kind === "ReplaceWithMessageResult")
  {
    switch (workTypeResult.kind) {
      case "ShowOrHideOptionResult":
        return workTypeResult.shouldShowOption
          ? [{ value: workType, label: label, subLabel: subLabel }]
          : [];
      case "ReplaceWithMessageResult":
        return [
          {
            value: workType,
            label: label,
            subLabel: workTypeResult.message,
            url: workTypeResult.url ?? undefined,
            disabled: true,
          },
        ];
      case "ShowWithMessageResult":
        return [
          {
            value: workType,
            label: label,
            subLabel: subLabel,
            message: workTypeResult.message,
            url: workTypeResult.url ?? undefined,
          },
        ];
      default:
        assertNever(workTypeResult);
    }
  }
  else
    return [];
  };

  const requiredWorkOptions: Checkbox<WorkType>[] = availableWorkTypesAndOptions
    ? [
        ...getCheckboxOptionForWorkType(
          availableWorkTypesAndOptions.DbmBookingWorkTypeService,
          WorkType.Service,
          t("required_work_service")
        ),
        ...getCheckboxOptionForWorkType(
          availableWorkTypesAndOptions.DbmBookingWorkTypeTyres,
          WorkType.Tyres,
          t("required_work_tyres")
        ),
        ...getCheckboxOptionForWorkType(
          availableWorkTypesAndOptions.DbmBookingWorkTypeMot,
          WorkType.Mot,
            countryCode === "DK"
            ? t("required_work_vehicle_check")
            : t("required_work_mot"),
          getMotDueDateLabel()
        ),
        ...getCheckboxOptionForWorkType(
          availableWorkTypesAndOptions.DbmBookingWorkTypeRepair,
          WorkType.RepairOther,
          t("required_work_repair")
        ),
      ]
    : [];

  // Region: Additional services
  const getDeliveryAndCollectionOption = (
    opts:
      | {
          [reference in keyof typeof RuleTableReference]: ShowHideOrMessageResult;
        }
      | null
  ) => {
    if (!opts) return [];
    const r = opts.DbmBookingBookingTypeDeliveryAndCollection;
    if (r.kind === "ShowOrHideOptionResult" && r.shouldShowOption)
      return [
        {
          value: AdditionalServiceType.Delivery,
          label: t("required_work_collection_delivery"),
        },
      ];
    if (r.kind === "ShowWithMessageResult")
      return [
        {
          value: AdditionalServiceType.Delivery,
          label: t("required_work_collection_delivery"),
          message: r.message,
          url: r.url,
        },
      ];
    return [];
  };

  const getCourtesyCarOption = (
    opts:
      | {
          [reference in keyof typeof RuleTableReference]: ShowHideOrMessageResult;
        }
      | null
  ) => {
    if (!opts) return [];
    const r = opts.DbmBookingBookingTypeCourtesyCar;
    if (r.kind === "ShowOrHideOptionResult" && r.shouldShowOption)
      return [
        {
          value: AdditionalServiceType.Courtesy,
          label: t("required_work_courtesy_car"),
        },
      ];
    if (r.kind === "ShowWithMessageResult")
      return [
        {
          value: AdditionalServiceType.Courtesy,
          label: t("required_work_courtesy_car"),
          message: r.message,
          url: r.url,
        },
      ];
    return [];
  };

  const getWhileUWaitOption = (
    opts:
      | {
          [reference in keyof typeof RuleTableReference]: ShowHideOrMessageResult;
        }
      | null
  ) => {
    if (!opts) return [];
    const r = opts.DbmBookingBookingTypeWhileUWait;
    if (r.kind === "ShowOrHideOptionResult" && r.shouldShowOption)
      return [
        {
          value: AdditionalServiceType.WhileUWait,
          label: t("required_work_whileuwait"),
        },
      ];
    if (r.kind === "ShowWithMessageResult")
      return [
        {
          value: AdditionalServiceType.WhileUWait,
          label: t("required_work_whileuwait"),
          message: r.message,
          url: r.url,
        },
      ];
    return [];
  };

  const getDropOffVehicleOption = (
    opts:
      | {
          [reference in keyof typeof RuleTableReference]: ShowHideOrMessageResult;
        }
      | null
  ) => {
    if (!opts) return [];
    const r = opts.DbmBookingBookingTypeDropOffVehicle;
    if (r.kind === "ShowOrHideOptionResult" && r.shouldShowOption)
      return [
        {
          value: AdditionalServiceType.NoAdditional,
          label: t("required_work_drop_off"),
        },
      ];
    if (r.kind === "ShowWithMessageResult")
      return [
        {
          value: AdditionalServiceType.NoAdditional,
          label: t("required_work_drop_off"),
          message: r.message,
          url: r.url,
        },
      ];
    return [];
  };

  const additionalServicesOptions: RadioButtonProps[] =
    availableWorkTypesAndOptions
      ? [
          ...getDeliveryAndCollectionOption(availableWorkTypesAndOptions),
          ...getCourtesyCarOption(availableWorkTypesAndOptions),
          ...getWhileUWaitOption(availableWorkTypesAndOptions),
          ...getDropOffVehicleOption(availableWorkTypesAndOptions),
        ]
      : [];
  // End Region Additional Services

  const initialValues: FormValues = {
    requiredWork: props.currentWorkDetails?.workRequired ?? [],
    repairOtherText: props.currentWorkDetails?.repairOtherText ?? "",
    safetyRecall: props.currentWorkDetails?.safetyRecall ?? false,
    additionalServices:
      props.currentWorkDetails?.additionalServiceRequested ??
      AdditionalServiceType.NoAdditional,
    collectionAddress: props.currentWorkDetails?.collectionAddress ?? "",
    collectionPostCode: props.currentWorkDetails?.collectionPostCode ?? "",
    collectionContactName:
      props.currentWorkDetails?.collectionContactName ?? "",
    collectionContactNumber:
      props.currentWorkDetails?.collectionContactNumber ?? "",
    collectionInstructions:
      props.currentWorkDetails?.collectionInstruction ?? "",
    additionalNotes: props.currentWorkDetails?.additionalNotes ?? "",
    collectionAddressSearch:
      props.currentWorkDetails?.collectionAddressSearch ?? "",
  };

     const handleSubmit = (
        values: FormValues,
        { setSubmitting }: { setSubmitting: (x: boolean) => void }
      ) => {
        setSubmitting(true);

        if (values.additionalServices !== AdditionalServiceType.Delivery) {
          clearTempCollection();
        }

        props.setWorkDetails({
          ...(props.currentWorkDetails ?? {}),
          workRequired: values.requiredWork,
          additionalServiceRequested: values.additionalServices,
          repairOtherText: values.repairOtherText?.trim() || undefined,
          collectionAddress: values.collectionAddress,
          collectionPostCode: values.collectionPostCode,
          collectionContactName: values.collectionContactName,
          collectionContactNumber: values.collectionContactNumber,
          collectionInstruction: values.collectionInstructions,
          requestedBookingDates:
          props.currentWorkDetails?.requestedBookingDates ?? [],
          additionalNotes: values.additionalNotes,
          safetyRecall: values.safetyRecall,
          safetyRecallText: values.safetyRecall ? SAFETY_RECALL_MESSAGE(t) : "",
          collectionAddressSearch: values.collectionAddressSearch,
        });
      props.goToNextStep();
    };

  const selectCollectionPostCode = async (id: string) => {
      const addressDetails = await retrieveAddressDetails(id);    
      let addressPostCode =
      config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION ===
      config.ONELINK_LOCATION_SERVICE
        .ONELINK_LOCATION_SERVICE_INTEGRATION_ENABLED
        ? addressDetails.items[0].postalCode
        : addressDetails.Items[0].PostalCode;

      if (countryCode == "DK") {
          addressPostCode = addressDetails.items[0].postalCode + " " + addressDetails.items[0].city;
      }
    return addressPostCode;
  };

  const retrieveAddressDetails = (id: string) => {
    const command: CommandDetails = new CommandDetails();
    let payload: any = {};
    if (
      config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION ===
      config.ONELINK_LOCATION_SERVICE
        .ONELINK_LOCATION_SERVICE_INTEGRATION_ENABLED
    ) {
      payload = {
        countryCode: countryCode === "DK" ? "DNK" : "GBR",
      };
      command.baseUrl =
        config.ONELINK_LOCATION_SERVICE.BASE_FIND_ADDRESS_URL + `/${id}?`;
    } else {
      payload = {
        Key: config.LOQATE_KEY,
        Id: id,
      };
      command.baseUrl = LoqateConfig.BaseRetrieveUrl;
    }
    command.payload = payload;
    const api: Api = new Api(command);

    const result = api.Get();
    return result;
  };
  
  const callAddressLookupApi = (inputText: string, container?: string) => {
    const command: CommandDetails = new CommandDetails();
    let payload: any = {};
    if (
      config.ONELINK_LOCATION_SERVICE.ONELINK_LOCATION_SERVICE_INTEGRATION ===
        config.ONELINK_LOCATION_SERVICE
          .ONELINK_LOCATION_SERVICE_INTEGRATION_ENABLED &&
      inputText !== null &&
      inputText !== ""
    ) {
      {
        payload = {
          Container: container ?? "",
          Countries: countryCode === "DK" ? "DNK" : "GBR",
        };
        command.baseUrl =
          config.ONELINK_LOCATION_SERVICE
            .BASE_ADDRESS_CAPTURE_FROM_POSTCODE_URL + `/${inputText}?`;
      }
    } else {
      payload = {
        Key: config.LOQATE_KEY,
        Text: inputText,
        Countries: LoqateConfig.Countries,
        Language: LoqateConfig.Language,
        Container: container ?? "",
        Limit: LoqateConfig.Limit,
      };
      command.baseUrl = LoqateConfig.BaseFindUrl;
    }
    command.payload = payload;
    const api: Api = new Api(command);

    const result = api.Get();
    return result;
  };

  useEffect(() => {
    const allDisabled = requiredWorkOptions.filter(item => item.disabled === true).length === requiredWorkOptions.length;
    setDisableSubmit(availableWorkTypesAndOptions != null && (requiredWorkOptions.length === 0 || allDisabled));
    if(availableWorkTypesAndOptions && (requiredWorkOptions.length === 0 || allDisabled) && (props.noBookingOptionsHelptext || props.noBookingOptionsHelptextUrl))
    {
      console.log("requiredWorkOptions.length: " , requiredWorkOptions.length);
      console.log("availableWorkTypesAndOptions: " , availableWorkTypesAndOptions);
      console.log("requiredWorkOptions: " , requiredWorkOptions);
      const isDisable = requiredWorkOptions.filter(item => item.disabled === true).length;
      console.log("daibled filter: ", isDisable)
      console.log("allDisabled: ", allDisabled);
      setShowModal(
            <ModalCentered
              size="sm"
              show={true}
              onHide={() => setShowModal(null)}
            >
              <div className="custom-dialog">
                <Modal.Header closeButton onHide={() => setShowModal(null)} />
                <div>
                  {(() => {
                    if (
                      props.noBookingOptionsHelptextUrl !== null &&
                      props.noBookingOptionsHelptextUrl !== undefined &&
                      props.noBookingOptionsHelptextUrl.trim() !== ""
                    ) {
                      return (
                        <a
                          href={props.noBookingOptionsHelptextUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {props.noBookingOptionsHelptext}
                        </a>
                      );
                    }
                    return props.noBookingOptionsHelptext;
                  })()}
                </div>
              </div>
            </ModalCentered>
          );
    }

    additionalServicesOptions
      .filter((option) => option.selected)
      .forEach((element) => {
        if (element.message || element.url) {
          setShowModal(
            <ModalCentered
              size="sm"
              show={true}
              onHide={() => setShowModal(null)}
            >
              <div className="custom-dialog">
                <Modal.Header closeButton onHide={() => setShowModal(null)} />
                <div>
                  {(() => {
                    const radioButtonValue = element;
                    if (
                      radioButtonValue.url !== null &&
                      radioButtonValue.url !== undefined &&
                      radioButtonValue.url.trim() !== ""
                    ) {
                      return (
                        <a
                          href={radioButtonValue.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {radioButtonValue.message}
                        </a>
                      );
                    }
                    return radioButtonValue.message;
                  })()}
                </div>
              </div>
            </ModalCentered>
          );
        }
      });
  }, [availableWorkTypesAndOptions]);

  const [addrQuery, setAddrQuery] = useState("");

  useEffect(() => {
    const q =
      props.currentWorkDetails?.collectionAddressSearch ??
      props.currentWorkDetails?.collectionPostCode ??
      "";
    setAddrQuery(q);
  }, [
    props.currentWorkDetails?.collectionAddressSearch,
    props.currentWorkDetails?.collectionPostCode,
  ]);


  const DIRECT_GROUP_ID = "__DIRECT_ADDR__";
  
  const fetchGroupSuggestions: AddressSearchProps["fetchGroupSuggestions"] = async (q) => {
    if (!q?.trim()) return [];
    const res = await callAddressLookupApi(q);
    const items = (res?.Items ?? []) as Array<{
      Id: string;
      Text: string;
      Description: string;
      Type: string;
    }>;

    const isAddr = (t: string) => LoqateConfig.isAnAddress(t);

    const addressHits = items.filter(i => isAddr(i.Type));
    const nonAddressItems = items.filter(i => !isAddr(i.Type));
    const rawGroups = mapLoqateGroupsGeneric(nonAddressItems, {
      formatDescription: LoqateConfig.formatDescription,
      isAddressType: LoqateConfig.isAnAddress,
    });

    const groups = rawGroups.map(g => ({
      ...g,
      label: stripTrailingCount(stripLeadingDuplicate(g.code, g.label)),
    }));

    if (groups.length > 0) {
      return groups;
    }

    if (addressHits.length > 0) {
      return [{
        id: DIRECT_GROUP_ID,
        code: "",
        label: "Results",
        count: addressHits.length,
      }];
    }

    return [];
  };

  const fetchDetailSuggestions: AddressSearchProps["fetchDetailSuggestions"] = async (groupId) => {
    const res = await callAddressLookupApi(groupId === DIRECT_GROUP_ID ? addrQuery : addrQuery, groupId === DIRECT_GROUP_ID ? undefined : groupId);
    const items = (res?.Items ?? []) as Array<{
      Id: string;
      Text: string;
      Description: string;
      Type: string;
    }>;

    const toDetail = (i: { Id: string; Text: string; Description: string }) => ({
      id: i.Id,
      label: `${i.Text} ${LoqateConfig.formatDescription(i.Description)}`.trim(),
    });

    if (groupId === DIRECT_GROUP_ID) {
      return items.filter(i => LoqateConfig.isAnAddress(i.Type)).map(toDetail);
    }

    return items.filter(i => LoqateConfig.isAnAddress(i.Type)).map(toDetail);
  };

  return (
    <div className="row no-gutters">
      <div className="col-12">
        <Hero
          heading={t("required_work_work_required")}
          description={t("required_work_work_required_desc")}
        />
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={schema}
          enableReinitialize={false}
        >
          {({ values, isSubmitting, setFieldValue, errors, submitCount }) => (
            <div>
              <FormikForm>
                <div className="form-group work-required-container">
                  {evaluationInProgress ? (
                    <ContentLoader
                      speed={1}
                      width={832}
                      height={170}
                      viewBox="0 0 832 170"
                      backgroundColor="#f3f3f3"
                      foregroundColor="#ecebeb"
                    >
                      <rect x="0" y="3" rx="0" ry="0" width="832" height="70" />
                      <rect
                        x="0"
                        y="78"
                        rx="0"
                        ry="0"
                        width="832"
                        height="70"
                      />
                    </ContentLoader>
                  ) : (
                    <CheckboxGroup
                      className="checkbox-line col-12"
                      name="requiredWork"
                      checkboxes={requiredWorkOptions}
                      selectedValues={values.requiredWork}
                    />
                  )}
              
                  {values.requiredWork?.includes(WorkType.RepairOther) ? (
                    <FormikFormRow
                      name="repairOtherText"
                      as="textarea"
                      placeholder={t("required_work_work_enter_desc")}
                      maxLength={500}
                      inputClassName="bottom-textarea"
                    />
                  ) : null}

                  {values.requiredWork?.includes(WorkType.RepairOther) &&
                    props.safetyRecallCheck && (
                      <div className="form-group safety-recall-checkbox mt-3">
                        <Field
                          type="checkbox"
                          name="safetyRecall"
                          id="safetyRecall"
                        />
                        <label
                          htmlFor="safetyRecall"
                          className="d-flex align-items-center"
                        >
                          {t("required_work_work_repair_checkbox_label")}
                        </label>
                      </div>
                    )}
                </div>

                <div className="form-group additional-work-group">
                  <h5>{t("required_work_work_booking_type")}</h5>
                  <RadioButtonGroup
                    name="additionalServices"
                    radioButtonValues={additionalServicesOptions}
                    orientation="horizontal"
                  />
                </div>

                {values.additionalServices === AdditionalServiceType.Delivery ? (
                    <>
                      <FormikFormRow
                        name="collectionContactName"
                        label={t("required_work_contact_name_label")}
                      />
                      <FormikFormRow
                        name="collectionContactNumber"
                        label={t("required_work_contact_number")}
                      />

                      <div className="col-12 p-0">
                        <div className="address-search-field">
                          <AddressSearch
                            id="collection-addr"
                            value={addrQuery}
                            onChange={(next) => {
                              setAddrQuery(next);
                              setFieldValue("collectionAddressSearch", next);
                            }}
                            onClear={() => {
                              setAddrQuery("");
                              setFieldValue("collectionAddressSearch", "");
                              setFieldValue("collectionPostCode", "");
                              setFieldValue("collectionAddress", "");
                              sessionStorage.removeItem("tempCollectionPostcode");
                              sessionStorage.removeItem("tempCollectionAddress");
                            }}
                            fetchGroupSuggestions={fetchGroupSuggestions}
                            fetchDetailSuggestions={fetchDetailSuggestions}
                            onSelectDetail={async (d: DetailSuggestion) => {
                              const pc = await selectCollectionPostCode(d.id);
                              sessionStorage.setItem("tempCollectionPostcode", pc);
                              sessionStorage.setItem("tempCollectionAddress", d.label);
                              setFieldValue("collectionAddress", d.label);
                              setFieldValue("collectionPostCode", pc);
                              setFieldValue("collectionAddressSearch", pc);
                              setAddrQuery(pc);
                            }}
                            strings={makeDefaultStrings(t)}
                            label={t("required_work_collection_postcode")}
                            keepPostcodeOnSelect
                            debounceMs={DEBOUNCE_MS}
                            minChars={MIN_CHARS}
                            ariaDescribedBy="collection-addr-hint"
                            suppressFirstOpen={true}
                          />
                          
                          {values.additionalServices === AdditionalServiceType.Delivery &&
                            submitCount > 0 &&
                            errors.collectionAddress ? (
                            <div className="error-msg">{errors.collectionAddress}</div>
                          ) : null}
                          <div id="collection-addr-hint" className="sr-only">
                            {t("required_work_type_address")}
                          </div>
                        </div>
                      </div>

                      {values.collectionAddress ? (
                      <div className="mb-4 mt-3">
                        <label className="form-label">
                          {t("address_search_type_selected_address")}
                        </label>
                        <textarea
                          className="form-control md-light"
                          readOnly
                          rows={4}
                          value={values.collectionAddress}
                        />
                      </div>
                    ) : null}

                      <FormikFormRow
                        name="collectionInstructions"
                        label={t("required_work_type_collection_instructions")}
                      />

                      {/* keep hidden so Formik submits them */}
                      <Field type="hidden" name="collectionAddress" />
                      <Field type="hidden" name="collectionPostCode" />
                      <Field type="hidden" name="collectionAddressSearch" />
                    </>
                ) : null}

                <DropdownStyleExpander
                  headerLabel={t("required_work_type_additional_notes")}
                >
                  <div className="col-12">
                    <FormikField
                      name="additionalNotes"
                      label={t("required_work_type_additional_notes_desc")}
                      as="textarea"
                      maxLength={2000}
                    />
                  </div>
                </DropdownStyleExpander>

                <Button
                  type="submit"
                  variant="primary"
                  className="large-input"
                  disabled={isSubmitting || disableSubmit}
                >
                  {t("required_work_type_continue")}
                </Button>
              </FormikForm>
            </div>
          )}
        </Formik>
      </div>
      {showModal}
    </div>
  );
};
